/**
 * MapPanel Component - Intelligent Google Maps Integration
 * Displays UK locations, addresses, NHS services, Job centres on a slide-in panel
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './MapPanel.css';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Location types for UK-specific searches
const UK_LOCATION_TYPES = {
  NHS: ['hospital', 'doctor', 'health', 'pharmacy', 'dentist', 'clinic'],
  JOBCENTRE: ['jobcentre', 'job centre', 'employment', 'dwp'],
  ADDRESS: ['address', 'location', 'directions', 'where is', 'how to get to', 'find'],
  COUNCIL: ['council', 'local authority', 'town hall', 'civic'],
  IMMIGRATION: ['home office', 'visa', 'immigration', 'ukvi']
};

// Extract location/address from AI response or user query
export const extractLocationFromText = (text) => {
  if (!text) return null;

  const lowerText = text.toLowerCase();

  // UK Postcode pattern (e.g., SW1A 1AA, M1 1AA, B1 1BB)
  const postcodeRegex = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/gi;
  const postcodeMatch = text.match(postcodeRegex);

  // Address patterns (street names, building names)
  const addressPatterns = [
    /\d+\s+[\w\s]+(?:street|road|lane|avenue|drive|way|close|crescent|place|square|gardens|grove|terrace|court|parade)\b/gi,
    /(?:the\s+)?[\w\s]+(?:hospital|surgery|clinic|centre|center|office|building|house|hall)\b/gi,
    /(?:nhs\s+)?[\w\s]+(?:trust|medical|health)\b/gi
  ];

  // Check for NHS/Healthcare locations
  const isHealthcare = UK_LOCATION_TYPES.NHS.some(term => lowerText.includes(term));

  // Check for Job Centre locations
  const isJobcentre = UK_LOCATION_TYPES.JOBCENTRE.some(term => lowerText.includes(term));

  // Check for immigration-related
  const isImmigration = UK_LOCATION_TYPES.IMMIGRATION.some(term => lowerText.includes(term));

  // Check for general address query
  const isAddressQuery = UK_LOCATION_TYPES.ADDRESS.some(term => lowerText.includes(term));

  // Extract specific location names from text
  let location = null;

  // First try postcode
  if (postcodeMatch && postcodeMatch.length > 0) {
    location = postcodeMatch[0].toUpperCase();
  }

  // Try address patterns
  if (!location) {
    for (const pattern of addressPatterns) {
      const match = text.match(pattern);
      if (match && match[0].length > 5) {
        location = match[0].trim();
        break;
      }
    }
  }

  // Extract UK city names
  const ukCities = [
    'london', 'manchester', 'birmingham', 'leeds', 'glasgow', 'liverpool',
    'bristol', 'sheffield', 'edinburgh', 'leicester', 'coventry', 'bradford',
    'cardiff', 'belfast', 'nottingham', 'newcastle', 'sunderland', 'brighton',
    'hull', 'plymouth', 'stoke', 'wolverhampton', 'derby', 'southampton',
    'portsmouth', 'oxford', 'cambridge', 'york', 'peterborough', 'luton',
    'bolton', 'blackpool', 'middlesbrough', 'stockport', 'reading', 'croydon'
  ];

  const foundCity = ukCities.find(city => lowerText.includes(city));

  // Determine location type
  let locationType = 'general';
  if (isHealthcare) locationType = 'healthcare';
  else if (isJobcentre) locationType = 'jobcentre';
  else if (isImmigration) locationType = 'immigration';

  // Build search query
  let searchQuery = location || foundCity;

  if (!searchQuery && isAddressQuery) {
    // Try to extract any place name mentioned
    const placeMatch = text.match(/(?:in|at|near|to)\s+([\w\s]+?)(?:\.|,|$)/i);
    if (placeMatch) {
      searchQuery = placeMatch[1].trim();
    }
  }

  // Add UK context to search
  if (searchQuery && !searchQuery.toLowerCase().includes('uk')) {
    searchQuery = `${searchQuery}, UK`;
  }

  // Return location info if we found something meaningful
  if (searchQuery || isHealthcare || isJobcentre || isImmigration) {
    return {
      query: searchQuery,
      type: locationType,
      city: foundCity,
      postcode: postcodeMatch ? postcodeMatch[0] : null,
      isHealthcare,
      isJobcentre,
      isImmigration,
      originalText: text.substring(0, 200)
    };
  }

  return null;
};

// Detect if a message should trigger map display
// ONLY triggers for explicit location/direction requests from the USER
export const shouldShowMap = (text) => {
  if (!text) return false;

  const lowerText = text.toLowerCase();

  // Only show map when user explicitly asks for location/directions/address
  // Must contain a location intent keyword AND a place/service reference
  const locationIntentKeywords = [
    'where is', 'where are', 'where can i find', 'how to get to', 'how do i get to',
    'directions to', 'navigate to', 'address of', 'address for',
    'nearest', 'closest', 'near me', 'nearby',
    'find me', 'show me on map', 'show on map', 'map of',
    'locate', 'location of'
  ];

  const hasLocationIntent = locationIntentKeywords.some(kw => lowerText.includes(kw));

  // Check for postcode pattern (always a location request)
  const hasPostcode = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i.test(text);

  return hasLocationIntent || hasPostcode;
};

const MapPanel = ({ isOpen, onClose, locationData, searchQuery }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [places, setPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Load Google Maps script
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Google Maps API key not configured');
      setIsLoading(false);
      return;
    }

    if (window.google && window.google.maps) {
      setIsLoading(false);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoading(false);
    script.onerror = () => {
      setError('Failed to load Google Maps');
      setIsLoading(false);
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Initialize map when panel opens
  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google || isLoading) return;

    // Default to UK center
    const ukCenter = { lat: 54.5, lng: -2 };

    const map = new window.google.maps.Map(mapRef.current, {
      center: ukCenter,
      zoom: 6,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ],
      mapTypeControl: false,
      fullscreenControl: true,
      streetViewControl: true
    });

    mapInstanceRef.current = map;

    // Search for location if provided
    if (searchQuery || (locationData && locationData.query)) {
      searchLocation(locationData?.query || searchQuery);
    }
  }, [isLoading, locationData, searchQuery]);

  // Search for location and display on map
  const searchLocation = useCallback((query) => {
    if (!mapInstanceRef.current || !window.google || !query) return;

    const service = new window.google.maps.places.PlacesService(mapInstanceRef.current);

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Build search request based on location type
    let request = {
      query: query,
      region: 'uk'
    };

    // Add type filter for specific searches
    if (locationData?.isHealthcare) {
      request.type = 'hospital';
    } else if (locationData?.isJobcentre) {
      request.query = `Jobcentre Plus ${query}`;
    }

    service.textSearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
        setPlaces(results.slice(0, 5));

        // Create bounds to fit all results
        const bounds = new window.google.maps.LatLngBounds();

        results.slice(0, 5).forEach((place, index) => {
          const marker = new window.google.maps.Marker({
            map: mapInstanceRef.current,
            position: place.geometry.location,
            title: place.name,
            label: {
              text: (index + 1).toString(),
              color: 'white',
              fontWeight: 'bold'
            },
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 15,
              fillColor: locationData?.isHealthcare ? '#10a37f' :
                        locationData?.isJobcentre ? '#3b82f6' : '#ef4444',
              fillOpacity: 1,
              strokeColor: 'white',
              strokeWeight: 2
            }
          });

          // Add click listener for info window
          marker.addListener('click', () => {
            setSelectedPlace(place);
            mapInstanceRef.current.panTo(place.geometry.location);
            mapInstanceRef.current.setZoom(15);
          });

          markersRef.current.push(marker);
          bounds.extend(place.geometry.location);
        });

        // Fit map to show all markers
        mapInstanceRef.current.fitBounds(bounds);

        // If only one result, zoom in more
        if (results.length === 1) {
          mapInstanceRef.current.setZoom(15);
        }
      } else {
        console.log('Places search failed:', status);
        // Try geocoding as fallback
        geocodeLocation(query);
      }
    });
  }, [locationData]);

  // Fallback geocoding for addresses
  const geocodeLocation = (address) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address, region: 'uk' }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        mapInstanceRef.current.setCenter(location);
        mapInstanceRef.current.setZoom(15);

        const marker = new window.google.maps.Marker({
          map: mapInstanceRef.current,
          position: location,
          title: results[0].formatted_address
        });
        markersRef.current.push(marker);

        setPlaces([{
          name: results[0].formatted_address,
          formatted_address: results[0].formatted_address,
          geometry: results[0].geometry
        }]);
      }
    });
  };

  // Initialize map when panel opens
  useEffect(() => {
    if (isOpen && !isLoading) {
      setTimeout(initializeMap, 100);
    }
  }, [isOpen, isLoading, initializeMap]);

  // Handle getting directions
  const handleGetDirections = (place) => {
    if (!place || !place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="map-panel"
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        >
          {/* Header */}
          <div className="map-panel-header">
            <div className="map-panel-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>Location Map</span>
            </div>
            <button className="map-panel-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Search Info */}
          {(searchQuery || locationData?.query) && (
            <div className="map-search-info">
              <span className="search-label">Searching for:</span>
              <span className="search-query">{locationData?.query || searchQuery}</span>
              {locationData?.type && locationData.type !== 'general' && (
                <span className={`search-type ${locationData.type}`}>
                  {locationData.type === 'healthcare' && 'Healthcare'}
                  {locationData.type === 'jobcentre' && 'Job Centre'}
                  {locationData.type === 'immigration' && 'Immigration'}
                </span>
              )}
            </div>
          )}

          {/* Map Container */}
          <div className="map-container">
            {isLoading && (
              <div className="map-loading">
                <div className="map-loading-spinner"></div>
                <span>Loading map...</span>
              </div>
            )}
            {error && (
              <div className="map-error">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            <div ref={mapRef} className="google-map"></div>
          </div>

          {/* Places List */}
          {places.length > 0 && (
            <div className="places-list">
              <h4>Nearby Locations</h4>
              {places.map((place, index) => (
                <div
                  key={index}
                  className={`place-item ${selectedPlace === place ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedPlace(place);
                    if (mapInstanceRef.current && place.geometry) {
                      mapInstanceRef.current.panTo(place.geometry.location);
                      mapInstanceRef.current.setZoom(15);
                    }
                  }}
                >
                  <div className="place-number">{index + 1}</div>
                  <div className="place-info">
                    <div className="place-name">{place.name}</div>
                    <div className="place-address">{place.formatted_address || place.vicinity}</div>
                    {place.rating && (
                      <div className="place-rating">
                        <span className="stars">{'★'.repeat(Math.round(place.rating))}</span>
                        <span className="rating-value">{place.rating}</span>
                      </div>
                    )}
                  </div>
                  <button
                    className="directions-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGetDirections(place);
                    }}
                    title="Get directions"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Continue Chatting Hint */}
          <div className="map-panel-footer">
            <span>Continue chatting while viewing the map</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default MapPanel;
